import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckCircleFilled, WarningFilled } from "@ant-design/icons";
import { App, Button, Form, Input, Modal, theme } from "antd";
import { observer } from "mobx-react";
import { useState } from "react";
import { LicenseAllocationDisplay } from "./licenseAllocationDisplay.js";
import { RealCommerceError } from "../../../../../../exceptions/realCommerceError.js";
import { useStores } from "../../../../../../store/stores.js";
import { COLOR_ERROR_SOFT } from "../../../../../../style/antdTheme.js";
import { __ } from "../../../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../../../utils/matomoMask.js";
import { useAccountV2ErrorHandling } from "../../../../hooks/useAccountV2ErrorHandling.js";
import { InfoCard } from "../../../infoCard.js";
const LicenseKeyDeleteConfirm = observer(({ license, onClose })=>{
    const { token } = theme.useToken();
    const { message } = App.useApp();
    const { accountV2Store } = useStores();
    const [isBusy, setIsBusy] = useState(false);
    const handleRealCommerceError = useAccountV2ErrorHandling();
    // Activation accounting: connection-lost rows are excluded from the
    // "actively using" count because those sites are most likely no longer
    // running the plugin at all. Three distinct framings depending on the
    // split — see `consequence` below.
    const activations = Array.from(license.activations.values());
    const lostCount = activations.filter((a)=>a.isConnectionLost).length;
    const connectedCount = activations.length - lostCount;
    const onConfirm = async ()=>{
        setIsBusy(true);
        try {
            await accountV2Store.deleteLicense(license);
            message.success(__("License key deleted."));
            onClose();
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        } finally{
            setIsBusy(false);
        }
    };
    let consequenceLeftBorder;
    let consequenceNode;
    if (connectedCount > 0) {
        consequenceLeftBorder = COLOR_ERROR_SOFT;
        consequenceNode = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(WarningFilled, {
                    style: {
                        color: COLOR_ERROR_SOFT,
                        marginRight: 8
                    }
                }),
                /*#__PURE__*/ _jsx("strong", {
                    children: connectedCount === 1 ? __("1 website is using this license key right now.") : __("%s websites are using this license key right now.", connectedCount)
                }),
                " ",
                __("After deletion the plugin on those sites will no longer receive updates and can no longer load content from our cloud.")
            ]
        });
    } else if (lostCount > 0) {
        consequenceLeftBorder = token.colorWarning;
        consequenceNode = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(WarningFilled, {
                    style: {
                        color: token.colorWarning,
                        marginRight: 8
                    }
                }),
                lostCount === 1 ? __("1 website used this license key but hasn't been reachable for a while. The plugin was likely uninstalled - but if the site is still live, after deletion it will no longer receive updates and can no longer load content from our cloud.") : __("%s websites used this license key but haven't been reachable for a while. The plugin was likely uninstalled - but if any of them is still live, after deletion the plugin there will no longer receive updates and can no longer load content from our cloud.", lostCount)
            ]
        });
    } else {
        consequenceLeftBorder = token.colorSuccess;
        consequenceNode = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(CheckCircleFilled, {
                    style: {
                        color: token.colorSuccess,
                        marginRight: 8
                    }
                }),
                /*#__PURE__*/ _jsx("strong", {
                    children: __("Safe to delete.")
                }),
                " ",
                __("This license key isn't being used on any website.")
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Modal, {
        open: true,
        title: __("Delete license key"),
        footer: null,
        onCancel: isBusy ? undefined : onClose,
        maskClosable: !isBusy,
        width: 640,
        children: [
            /*#__PURE__*/ _jsx(Form, {
                layout: "vertical",
                disabled: isBusy,
                className: "rc-account-v2-license-form",
                children: /*#__PURE__*/ _jsx(Form.Item, {
                    label: __("License key"),
                    children: /*#__PURE__*/ _jsx(Input, {
                        value: license.data.licenseKey,
                        disabled: true,
                        ...MATOMO_MASK
                    })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    marginBottom: 16
                },
                children: /*#__PURE__*/ _jsx(InfoCard, {
                    variant: "soft-trust",
                    leftBorderColor: consequenceLeftBorder,
                    children: consequenceNode
                })
            }),
            (license.data.maxInstallationsProduction > 0 || license.data.maxInstallationsDevelopment > 0) && /*#__PURE__*/ _jsxs("div", {
                style: {
                    marginBottom: 16
                },
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "rc-account-v2-license-form__intro",
                        style: {
                            marginBottom: 8
                        },
                        children: __("After deletion, these licenses are free again and can be assigned to a new license key:")
                    }),
                    /*#__PURE__*/ _jsx(LicenseAllocationDisplay, {
                        production: license.data.maxInstallationsProduction,
                        development: license.data.maxInstallationsDevelopment
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-license-modal-footer rc-account-v2-license-modal-footer--paired",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: onClose,
                        disabled: isBusy,
                        children: __("Keep license key")
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        danger: true,
                        type: "primary",
                        loading: isBusy,
                        onClick: onConfirm,
                        children: __("Delete license key")
                    })
                ]
            })
        ]
    });
});
export { LicenseKeyDeleteConfirm };
